import * as React from 'react';
import { Outlet, useNavigate, useParams, useRevalidator } from 'react-router';
import DealerSelectionDialog from 'components/DealerSelectionDialog';
import { EErrorCode } from 'config/errors-codes';
import { brandSchema, marketSchema } from 'data/schema';
import { useError } from 'hooks/useError';
import { isAuthenticated } from 'utils/auth';
import { OPEN, PENDING, runDealerGate } from 'utils/auth/dealerGateFlow';
import type { IGateRun, TGateView } from 'utils/auth/dealerGateFlow';
import { user } from 'utils/auth/user';
import { sendApiRequest } from 'utils/network';

interface IDealerAccessResponse {
  allowed: boolean;
  code?: EErrorCode;
}

/**
 * Validates dealer access against the backend whitelist.
 * Only runs for dealer users when whitelisting might be enabled.
 */
async function validateDealerAccess(
  brand: string,
  market: string,
  dealerId: string
): Promise<{ allowed: boolean; code?: EErrorCode }> {
  const response = await sendApiRequest<IDealerAccessResponse>(`/dealer-access?brand=${brand}&market=${market}`, {
    headers: {
      'X-Dealer': dealerId,
    },
  });

  return response ?? { allowed: true };
}

/**
 * SecureOutlet is a wrapper around the react-router Outlet component.
 * It is the standalone dealer gate (AVS-4405): until the dealer is chosen and this brand and market's tenant config
 * allows access, it renders no routes, and no session or tenant data is loaded. The gate itself lives in
 * `utils/auth/dealerGateFlow`; this component only connects it to React and the router.
 */
export const SecureOutlet = () => {
  const throwError = useError();
  const { revalidate } = useRevalidator();
  const navigate = useNavigate();
  const { brand, market, sessionId } = useParams();
  const [view, setView] = React.useState<TGateView>(PENDING);

  // `useError()` returns a new function on every render, so the effect reads the latest through a ref
  // and re-runs only for a new brand or market.
  const latest = React.useRef({ throwError, revalidate, navigate, sessionId });
  latest.current = { throwError, revalidate, navigate, sessionId };

  React.useEffect(() => {
    let cancelled = false;
    setView(PENDING);

    // If the user is not authenticated, the dealer gate does not apply yet: let the user complete the
    // authentication flow (ProtectedRoute redirects), without waiting for any tenant config.
    if (!isAuthenticated()) {
      setView(OPEN);
      return;
    }

    // The route loader already rejects an unknown brand or market; this keeps the types honest without a cast.
    const parsedBrand = brandSchema.safeParse(brand);
    if (!parsedBrand.success) {
      latest.current.throwError(EErrorCode.INVALID_BRAND);
      return;
    }

    const parsedMarket = marketSchema.safeParse(market);
    if (!parsedMarket.success) {
      latest.current.throwError(EErrorCode.INVALID_MARKET);
      return;
    }

    const run: IGateRun = {
      brand: parsedBrand.data,
      market: parsedMarket.data,
      sessionId: latest.current.sessionId,
      dealerIds: user.isDealer ? user.dealerIdsList : [],
      isDealer: user.isDealer,
      readStoredDealerId: () => user.selectedDealerId,
      readSearch: () => window.location.search,
      validateAccess: validateDealerAccess,
      isCancelled: () => cancelled,
      show: (next) => {
        if (!cancelled) {
          setView(next);
        }
      },
      fail: (code, options) => {
        if (!cancelled) {
          latest.current.throwError(code, options);
        }
      },
      navigate: (path) => {
        if (!cancelled) {
          latest.current.navigate(path);
        }
      },
      revalidate: () => latest.current.revalidate(),
    };

    void runDealerGate(run);

    return () => {
      cancelled = true;
    };
  }, [brand, market]);

  if (view.status === 'pending') {
    return null;
  }

  if (view.status === 'choose') {
    return (
      <DealerSelectionDialog
        options={view.options}
        brand={view.brand}
        market={view.market}
        error={view.error}
        preselectedDealerId={view.preselectedDealerId}
        onSelect={view.onSelect}
      />
    );
  }

  return <Outlet />;
};
